/**
 * Attachment rendering for every messaging surface.
 *
 * Previously: the employer/referrer/applicant threads mapped attachments into
 * state but never rendered them — files sent through chat were invisible. This
 * module renders image previews, file cards with download affordances, pending
 * upload chips and a full-screen lightbox, identically for all roles.
 */

import React from 'react';
import { AlertTriangle, Download, FileText, ImageIcon, Loader, Lock, Paperclip, ShieldCheck, ShieldAlert, X, ZoomIn } from 'lucide-react';
import type { MessageAttachment, PendingAttachment } from './types';
import {
  attachmentKey,
  fileExtensionLabel,
  formatFileSize,
  isImageAttachment,
  linkHost,
} from './utils';
import { downloadFile, fetchAsBlobUrl } from '../shared/TicketChatComponents';

/* ─── Lightbox ────────────────────────────────────────────────────────────── */

export function ImageLightbox({
  url,
  name,
  onClose,
}: {
  url: string;
  name?: string;
  onClose: () => void;
}) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="msg-lightbox msg-scope"
      role="dialog"
      aria-modal="true"
      aria-label={name ? `Preview of ${name}` : 'Image preview'}
      onClick={onClose}
    >
      <div className="msg-lightbox-bar">
        <a
          className="msg-icon-btn msg-icon-btn--accent"
          href={url}
          target="_blank"
          rel="noreferrer"
          download={name}
          aria-label="Download image"
          title="Download image"
          onClick={(e) => e.stopPropagation()}
        >
          <Download size={18} />
        </a>
        <button
          type="button"
          className="msg-icon-btn msg-icon-btn--accent"
          onClick={onClose}
          aria-label="Close preview"
        >
          <X size={18} />
        </button>
      </div>
      <img src={url} alt={name || ''} onClick={(e) => e.stopPropagation()} />
    </div>
  );
}

/* ─── Auth-aware attachment access ────────────────────────────────────────── *
 * Ticket attachments are served from `/api/uploads/{fileId}`, which requires an
 * Authorization header. A bare `<img src>` / `<a href>` sends no header, so
 * applicants and employers used to get broken images and failed downloads
 * (admins already fetched with auth). Every attachment URL now resolves through
 * the authenticated blob-URL helper, so all roles behave identically.
 * ─────────────────────────────────────────────────────────────────────────── */

const AuthImage = React.memo(function AuthImage({
  att,
  onOpen,
}: {
  att: MessageAttachment;
  onOpen: (att: MessageAttachment) => void;
}) {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);
  const blobRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setBlobUrl(null);
    fetchAsBlobUrl(att.url).then((url) => {
      if (cancelled) {
        if (url) URL.revokeObjectURL(url);
        return;
      }
      if (url) {
        blobRef.current = url;
        setBlobUrl(url);
      } else {
        setFailed(true);
      }
    });
    return () => {
      cancelled = true;
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
      }
    };
  }, [att.url]);

  if (failed) {
    // Graceful fallback: a clear message and a working download instead of a
    // broken-image icon.
    return (
      <button
        type="button"
        className="msg-file msg-file--warning"
        onClick={() => downloadFile(att.url, att.name).catch(() => {})}
        title={`${att.name || 'Image'} — preview unavailable, click to download`}
        aria-label={`Preview unavailable for ${att.name || 'image'}. Download instead.`}
      >
        <span className="msg-file-icon"><ImageIcon size={16} /></span>
        <span className="msg-file-body">
          <span className="msg-file-name" title={att.name || undefined}>{att.name || 'Image'}</span>
          <span className="msg-file-sub">Preview unavailable — tap to download</span>
        </span>
        <Download size={15} className="msg-icon-btn" />
      </button>
    );
  }

  return (
    <button
      type="button"
      className="msg-image-btn"
      onClick={() => blobUrl && onOpen({ ...att, url: blobUrl })}
      disabled={!blobUrl}
      aria-label={`Open image ${att.name || ''}`}
      title={att.name || undefined}
    >
      {blobUrl ? (
        <img className="msg-image" src={blobUrl} alt={att.name || ''} loading="lazy" decoding="async" onError={() => setFailed(true)} />
      ) : (
        <span className="msg-image-loading" role="status" aria-label="Loading image">
          <Loader size={16} className="msg-spin" />
        </span>
      )}
    </button>
  );
});

AuthImage.displayName = 'AuthImage';

const AttachmentFileCard = React.memo(function AttachmentFileCard({
  att,
}: {
  att: MessageAttachment;
}) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleDownload = React.useCallback(async (e?: React.MouseEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await downloadFile(att.url, att.name);
    } catch {
      setError('Download failed — please try again.');
    } finally {
      setBusy(false);
    }
  }, [att.url, att.name]);

  const handleView = React.useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const url = await fetchAsBlobUrl(att.url);
      if (!url) throw new Error('unavailable');
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) throw new Error('blocked');
      // Give the new tab time to load before releasing the object URL.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError('This file could not be opened — try Download instead.');
    } finally {
      setBusy(false);
    }
  }, [att.url, att.name]);

  const name = att.name || 'Attachment';
  const canPreview = att.mimeType === 'application/pdf' || /\.pdf$/i.test(name);

  return (
    <div className="msg-file-wrap">
      <a
        className="msg-file"
        href={att.url}
        onClick={handleDownload}
        aria-label={`Download ${name}`}
        title={`Download ${name}`}
      >
        <span className="msg-file-icon">
          <FileText size={15} />
        </span>
        <span className="msg-file-body">
          <span className="msg-file-name" title={name}>{name}</span>
          <span className="msg-file-sub">
            {fileExtensionLabel(att)}
            {att.size ? ` · ${formatFileSize(att.size)}` : ''}
          </span>
        </span>
        {busy ? (
          <Loader size={15} className="msg-spin msg-icon-btn" />
        ) : (
          <Download size={15} className="msg-icon-btn" />
        )}
      </a>
      {canPreview && (
        <button
          type="button"
          className="msg-file-view"
          onClick={handleView}
          disabled={busy}
          aria-label={`Open ${name}`}
          title={`Open ${name}`}
        >
          <ZoomIn size={13} />
          <span>View</span>
        </button>
      )}
      {error && <p className="msg-file-error" role="alert">{error}</p>}
    </div>
  );
});

AttachmentFileCard.displayName = 'AttachmentFileCard';

/* ─── Message attachments ─────────────────────────────────────────────────── */

export interface MessageAttachmentsProps {
  attachments: MessageAttachment[];
  onPreviewImage?: (attachment: MessageAttachment) => void;
}

export const MessageAttachments = React.memo(function MessageAttachments({
  attachments,
  onPreviewImage,
}: MessageAttachmentsProps) {
  if (!attachments || attachments.length === 0) return null;

  return (
    <span className="msg-attachments">
      {attachments.map((att, i) => {
        const key = attachmentKey(att, i);
        const scanStatus = att.scanStatus || 'clean'; // Default to clean if backend didn't set status

        // 1. Scanning State
        if (scanStatus === 'scanning') {
          return (
            <div
              key={key}
              className="msg-file msg-file--scanning"
              aria-label={`Scanning ${att.name}`}
            >
              <span className="msg-file-icon">
                <ShieldCheck size={16} className="msg-spin" />
              </span>
              <span className="msg-file-body">
                <span className="msg-file-name">{att.name || 'Attachment'}</span>
                <span className="msg-file-sub">
                  Checking safety…
                </span>
              </span>
            </div>
          );
        }

        // 2. Infected / Quarantined State
        if (scanStatus === 'infected' || scanStatus === 'quarantined') {
          return (
            <div
              key={key}
              className="msg-file msg-file--danger"
              aria-label={`Blocked attachment ${att.name}`}
            >
              <span className="msg-file-icon">
                <ShieldAlert size={16} />
              </span>
              <span className="msg-file-body">
                <span className="msg-file-name msg-file-name--blocked">
                  {att.name || 'Attachment'}
                </span>
                <span className="msg-file-sub">This file couldn't be delivered</span>
              </span>
            </div>
          );
        }

        // 3. Failed State
        if (scanStatus === 'failed') {
          return (
            <div
              key={key}
              className="msg-file msg-file--warning"
              aria-label={`Upload failed for ${att.name}`}
            >
              <span className="msg-file-icon">
                <AlertTriangle size={16} />
              </span>
              <span className="msg-file-body">
                <span className="msg-file-name">{att.name || 'Attachment'}</span>
                <span className="msg-file-sub">Upload failed</span>
              </span>
            </div>
          );
        }

        // 4. Expired State
        if (scanStatus === 'expired') {
          return (
            <div
              key={key}
              className="msg-file msg-file--expired"
              aria-label={`Expired attachment ${att.name}`}
            >
              <span className="msg-file-icon">
                <FileText size={15} />
              </span>
              <span className="msg-file-body">
                <span className="msg-file-name">{att.name || 'Attachment'}</span>
                <span className="msg-file-sub">This file is no longer available</span>
              </span>
            </div>
          );
        }

        // 5. Clean / Normal State
        if (isImageAttachment(att)) {
          return <AuthImage key={key} att={att} onOpen={(next) => onPreviewImage?.(next)} />;
        }

        return <AttachmentFileCard key={key} att={att} />;
      })}
    </span>
  );
});

/* ─── Link preview ────────────────────────────────────────────────────────── */

export function LinkPreview({ url }: { url: string }) {
  const host = linkHost(url);
  if (!host) return null;
  return (
    <a className="msg-link-preview" href={url} target="_blank" rel="noreferrer noopener">
      <span className="msg-link-preview-host">{host}</span>
      <span className="msg-link-preview-url">{url}</span>
    </a>
  );
}

/* ─── Pending uploads (composer) ──────────────────────────────────────────── */

export function PendingAttachments({
  files,
  onRemove,
  uploadProgress,
  uploadError,
}: {
  files: PendingAttachment[];
  onRemove: (id: string) => void;
  uploadProgress?: number | null;
  uploadError?: string | null;
}) {
  if (files.length === 0 && !uploadError) return null;
  return (
    <div className="msg-pending-files-container">
      {uploadError && (
        <div className="msg-upload-error" role="alert">
          <AlertTriangle size={14} className="flex-shrink-0" />
          <span>{uploadError}</span>
        </div>
      )}
      {uploadProgress !== undefined && uploadProgress !== null && uploadProgress > 0 && uploadProgress < 100 && (
        <div className="msg-upload-progress-bar-wrap" aria-label={`Upload ${uploadProgress}% complete`}>
          <div className="msg-upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
        </div>
      )}
      {files.length > 0 && (
        <div className="msg-pending-files msg-pending-files--wrap" aria-label="Attachments ready to send">
          {files.map((file) => (
            <div key={file.id} className="msg-pending-file">
              {file.previewUrl ? (
                <img className="msg-pending-file-thumb" src={file.previewUrl} alt="" />
              ) : (
                <span className="msg-pending-file-icon">
                  {file.mimeType?.startsWith('image/') ? <ImageIcon size={13} /> : <Paperclip size={13} />}
                </span>
              )}
              <span className="msg-pending-file-body">
                <span className="msg-pending-file-name">{file.name}</span>
                <span className="msg-file-sub">{formatFileSize(file.size)}</span>
              </span>
              <button
                type="button"
                className="msg-icon-btn msg-icon-btn--sm"
                onClick={() => onRemove(file.id)}
                aria-label={`Remove ${file.name}`}
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Small affordance used by list rows: "📎 3 attachments". */
export function AttachmentCountHint({ count }: { count: number }) {
  if (!count) return null;
  return (
    <span className="msg-item-sub msg-attachment-count">
      <Paperclip size={11} />
      {count} attachment{count === 1 ? '' : 's'}
    </span>
  );
}

export { ZoomIn as AttachmentZoomIcon };
